/**
 * useUndoDelete Hook
 * Provides optimistic delete with 5-second undo window.
 * Removes task from Zustand immediately, then calls API delete after the
 * undo window expires. If the user clicks undo, the task is restored
 * and the API is never called.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Task } from '@signalvote/shared';
import { useTaskStore } from '@/stores';
import { useDeleteTask } from '@/hooks/queries';

// ============================================================================
// Types
// ============================================================================

interface PendingDelete {
  task: Task;
  timeoutId: number;
}

export interface UseUndoDeleteReturn {
  /** Trigger optimistic delete with undo capability */
  requestDelete: (task: Task) => void;
  /** Undo the pending delete and restore the task */
  undoDelete: () => void;
  /** Whether the undo snackbar should be shown */
  isSnackbarOpen: boolean;
  /** Title of the task pending deletion (for snackbar message) */
  pendingTaskTitle: string | undefined;
}

// ============================================================================
// Hook
// ============================================================================

export function useUndoDelete(): UseUndoDeleteReturn {
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const pendingRef = useRef<PendingDelete | null>(null);

  const deleteTask = useTaskStore((state) => state.deleteTask);
  const addTask = useTaskStore((state) => state.addTask);
  const { mutate: deleteTaskMutate } = useDeleteTask();

  // Keep ref in sync so callbacks always see latest value
  pendingRef.current = pendingDelete;

  /** Commit a pending delete to the backend (no undo possible after this) */
  const commitDelete = useCallback(
    (task: Task) => {
      deleteTaskMutate(task.id);
    },
    [deleteTaskMutate]
  );

  const requestDelete = useCallback(
    (task: Task) => {
      // If there's an existing pending delete, commit it immediately
      if (pendingRef.current) {
        clearTimeout(pendingRef.current.timeoutId);
        commitDelete(pendingRef.current.task);
      }

      // Optimistically delete from store
      deleteTask(task.id);

      // Schedule API delete after 5 seconds (undo window)
      const timeoutId = window.setTimeout(() => {
        commitDelete(task);
        setPendingDelete(null);
      }, 5000);

      // Store the deleted task for potential undo
      setPendingDelete({ task, timeoutId });
    },
    [deleteTask, commitDelete]
  );

  const undoDelete = useCallback(() => {
    if (pendingRef.current) {
      // Clear the auto-close timeout (prevents API call)
      clearTimeout(pendingRef.current.timeoutId);

      // Restore the task to the store
      addTask(pendingRef.current.task);

      // Clear pending state
      setPendingDelete(null);
    }
  }, [addTask]);

  // On unmount, commit any pending delete so it isn't lost
  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        clearTimeout(pendingRef.current.timeoutId);
        commitDelete(pendingRef.current.task);
      }
    };
  }, [commitDelete]);

  return {
    requestDelete,
    undoDelete,
    isSnackbarOpen: pendingDelete !== null,
    pendingTaskTitle: pendingDelete?.task.title,
  };
}

export default useUndoDelete;
